<template>
  <div
    class="overflow-hidden truncate placeholder-container"
    v-click-outside="blurHandler"
    @click="outerClick"
  >
    <AdminCellEmptyPlaceholder
      v-if="isEmpty && !isFocused"
      :outlined="btnOutlined"
    />
    <div v-else-if="isUpdating" class="d-flex">
      <v-progress-circular
        indeterminate
        size="15"
        width="2"
        color="grey"
        class="mx-auto"
      />
    </div>
    <v-chip
      v-else-if="!isFocused"
      :name="`${id}-${fieldName}`"
      :value="theRelation"
      v-bind="{ ...$attrs, ...$props }"
      color="#e9eef9"
      label
      :close="isFocused"
      class="proxima--font related-chip mr-3"
      >{{ foreignValue }}</v-chip
    >
    <v-combobox
      v-show="isFocused && !isUpdating"
      v-model="theRelation"
      class="single-select mt-0"
      no-data-text="Select"
      small-chips
      hide-details
      open-on-clear
      cache-items
      return-object
      autofocus
      :rules="{ ...$attrs, ...$props }.rules"
      :dense="denseSelect"
      :clearable="isNullable"
      :loading="isLoading"
      :items="items"
      :placeholder="placeholder"
      :item-text="foreignValueKey"
      :to="isFocused ? foreignLink : null"
      @keydown.esc="update"
      @change="update"
      @update:search-input="searchUpdate"
      v-tooltip="tooltipConfig"
    >
      <template #selection v-slot:selection="{}">
        <v-chip small :to="{ path: foreignLink }">{{ foreignValue }}</v-chip>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { Model } from '@vuex-orm/core'
import debounce from 'debounce'
import { ADMIN_URI_BASE } from '@/constants/RouteNames'

import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'

import AdminCellEmptyPlaceholder from '@/components/AdminCellEmptyPlaceholder'
export default {
  name: 'AdminInputRelated',
  components: {
    AdminCellEmptyPlaceholder,
  },
  props: {
    item: {
      type: Object,
      deafult: () => {},
      required: true,
    },
    header: {
      type: Object,
      default: () => {},
    },
    Model: {
      type: Model,
      default: () => Model,
    },
    id: {
      type: [String, Number],
      require: true,
    },
    sortKey: {
      type: String,
      default: null,
    },
    btnOutlined: {
      type: Boolean,
      default: false,
    },
    denseSelect: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    initFocused: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFocused: false,
      theRelation: null,
      isLoading: false,
      isUpdating: false,
      updateTimeout: null,
      showTooltip: false,
      items: [],
    }
  },
  created() {
    this.theRelation = this.foreignValue
    if (this.initFocused) {
      this.isFocused = true
    }
  },
  computed: {
    theModel() {
      return this.Model.query()
        .with(this.localModel)
        .where('id', this.id)
        .first()
    }, // theModel
    localModel() {
      return this.header.value.split('.')[0]
    },

    foreignModel() {
      return this.header.foreignModel
    },
    foreignEntity() {
      return this.foreignModel.find(this.foreignId)
    },
    foreignValueKey() {
      return this.header.value.split('.')[1] || this.localModel
    },

    foreignValue() {
      return _get(this.foreignEntity, this.foreignValueKey)
    }, // foreignValue
    foreignFieldDefinition() {
      return this.Model.fields()[this.localModel]
    }, // foreignFieldDefinition
    foreignLink() {
      return this.foreignId
        ? `/${ADMIN_URI_BASE}/${this.foreignFieldDefinition.parent.uri_path ||
            this.foreignFieldDefinition.parent.entity}/${this.foreignId}`
        : ''
    }, // foreignLink
    fieldName() {
      return this.Model.fields()[this.localModel].foreignKey
    }, // fieldName
    foreignId() {
      return _get(this.item, this.fieldName)
    }, // foreignId
    isEmpty() {
      return !this.theRelation
    }, // isEmpty
    width() {
      return this.header.width || null
    }, // width
    isNullable() {
      return this.Model.fields()[this.localModel].isNullable || false
    }, // isNullable
    tooltipConfig() {
      return {
        content:
          '<div class="proxima--font font-weight-bold">You must select an option</div>',
        shown: this.showTooltip,
        triggers: [],
        html: true,
      }
    }, // tooltipConfig
  }, // computed
  methods: {
    blurHandler() {
      if (!this.isFocused) return
      this.isFocused = false
    }, // blurHandler
    async loadItems(options) {
      this.items = []
      this.isLoading = true
      let defaults = { per_page: 100 }
      options = { ...defaults, ...options }
      try {
        await this.foreignModel.fetchForShop(options)
        const sortValue = this.sortKey || this.foreignValueKey
        this.items = _sortBy(this.foreignModel.all(), sortValue)
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    }, // loadItems
    searchUpdate: debounce(function(q) {
      if (!q) return
      this.loadItems({ q })
    }, 250), // tiptapUpdate
    update(selectedObject) {
      this.isUpdating = true
      this.updateTimeout = setTimeout(() => {
        this.isUpdating = false
      }, 6500)
      let newVal = _get(this.theRelation, this.foreignValueKey)
      if (!this.isNullable && !newVal) {
        newVal = this.foreignValue
        this.theRelation = this.foreignValue
        this.showTooltip = true
      }
      if (this.foreignValue == newVal) return

      const fields = { [this.fieldName]: _get(this.theRelation, 'id', null) }
      this.$emit('update', fields)
      this.$emit('update:object', selectedObject)
      this.isFocused = false
    }, // update
    outerClick() {
      if (this.isUpdating) {
        return
      } else {
        this.isFocused = true
      }
    }, // outerClick
  }, // methods
  watch: {
    isFocused(isFocused) {
      if (isFocused) {
        this.loadItems()
      }
    },
    foreignValue(foreignValue) {
      this.theRelation = foreignValue
      // when the value gets update through the prop/parent,
      // reset the updating status to false
      this.isUpdating = false
      clearTimeout(this.updateTimeout)
    },
  },
}
</script>

<style scoped>
.related-chip {
  cursor: pointer;
}
</style>
